import React, { useEffect, useRef } from 'react'
// import Swiper core and required modules
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'reactjs-popup/dist/index.css';

//import redux components
import { useSelector, useDispatch } from 'react-redux'
import { changeActiveCourse } from '../redux/activeCourseName/activeCourseNameSlice'

//SVG COMPONENTS - loaded with @svgr/webpack
import ArrowButton from '../../../../../img/icon-btn-arrow-right.svg';
import ArrowSwiperLeft from '../../../../../img/arrow-swiper-left.svg';
import ArrowSwiperRight from '../../../../../img/arrow-swiper-right.svg';

//  local components
import CourseMetaInfo from './courseMetaInfo'
import CourseHeroFooter from './courseHeroFooter'

/**
 * Time Line Hero (Swiper) component
 * @param {Object} courseDetailsProp - All the course details
 * @param {Object} courseNames - The names of the courses, used for indexing the swiper and page info
 */
function TimeLineHero({courseDetailsProp, courseNames}) {
    // get that redux dispacther!
    const dispatch = useDispatch();
    // get our active course name from the redux store
    const activeCourseName = useSelector((state) => state.activeCourseName.value)

    // the active course details
    const courseDetails = courseDetailsProp[activeCourseName];
    // whether this is an adjunct course or not, if it is, the swiper needs
    // to accomodate only having one slide
    const isAdjunctCourse = courseDetailsProp[activeCourseName].isAdjunctCourse || courseDetailsProp[activeCourseName].isNonCoreCourse;
    // if this is a distinction course
    const isDistinctionCourse = courseDetailsProp[activeCourseName].courseDistinctionId ? true : false;
    // if we should hide the pagination on the swiper
    const hidePagination = isAdjunctCourse && !isDistinctionCourse;
    // the name of the distinction
    const distinctionName = courseDetailsProp[activeCourseName].courseDistinctionName;

    // index of the prev course
    let prevCourse = courseNames.indexOf(activeCourseName) - 1;
    // index of the next course
    let nextCourse = courseNames.indexOf(activeCourseName) + 1;
    let courseIndex = 0;

    const divRef = useRef(null);

    // pagination data used for displaying the title and course number on the swiper
    const paginationData = Object.keys(courseDetailsProp)
    .map((key, index) => {
        // set the current index to match the active course
        if(key == activeCourseName){
            courseIndex = index;
        }

        return (
            {
                // set a title and course number for each index
                title: courseDetailsProp[key].title,
                courseNumber: courseDetailsProp[key].courseNumber,
                courseDistinctionSuggestedOrder: courseDetailsProp[key].courseDistinctionSuggestedOrder,
                key: key
            }
        );
    })
    .sort((a, b) => {
        // Keep the same order as courseNames by finding their indices
        return courseNames.indexOf(a.key) - courseNames.indexOf(b.key);
    });

    // video embed div for the swiper featured image
    const getVideoEmbed = (key) => {
        return (
            <div className="slide-feature__video" dangerouslySetInnerHTML={{__html: courseDetailsProp[key].heroVideo}}/>
        )
    }

    // currency formatter
    const formatter = new Intl.NumberFormat('en-US', {
       style: 'currency',
       currency: 'USD',
       // These options are needed to round to whole numbers if that's what you want.
       // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
       maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
    });

    // buil the custom swiper pagination component
    const swiperPagination = {
        clickable: true,
        el: '.swiper-pagination',
        renderBullet: function (index) {
            // if an adjunct course is being viewed there won't be any pagination 
            if (isAdjunctCourse && isDistinctionCourse){
                // set up the pagination to use the distinction course suggested order
                return (
                    '<span class="swiper-pagination-bullet" tabindex="0"><span class="title">' + paginationData[index].title + '</span><span class="num"><span>' + paginationData[index].courseDistinctionSuggestedOrder + '</span></span></span>'
                )
            } else if(isAdjunctCourse){
                return;
            } else {
            return (
                '<span class="swiper-pagination-bullet" tabindex="0"><span class="title">' + paginationData[index].title + '</span><span class="num"><span>' + paginationData[index].courseNumber + '</span></span></span>'
            )
            }
        }
    };

    // pause the swiper videos
    function pauseAllVideos() {
        const iframes = document.querySelectorAll('iframe');

        for (const iframe of iframes) {
            if(iframe.contentWindow){
                iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
            }
        }
    }

    // check if a DOM element is in the viewport
    function isOutOfView(element) {
        const rect = element.getBoundingClientRect()
        const scrollTop = window.scrollY;
        const scrollBot = window.innerHeight + scrollTop;
        const sectionTop = rect.top;

        return (
            sectionTop + element.offsetHeight < 0 ||
            scrollBot - 120 < sectionTop

        )

    }

    // remove loading classes
    useEffect(() => {
        document.querySelector('.timeline-hero').classList.add('loaded');
        document.querySelector('.timeline-hero').classList.remove('loading');
        document.addEventListener("click", () => {
            pauseAllVideos();
            });
        document.addEventListener("scroll", () => {
            const iframes = divRef.current.querySelectorAll('iframe');
            for (const iframe of iframes) {
                if (isOutOfView(iframe)) {
                    iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
                }
            }
        });
    }, []);

    // Component level JSX return statement ******************************************************
    return (
        <>
            <section className="timeline-hero loading">
    
                {/*  HEADER AREA  */}
                <div  className="course-details-header">
                    <div className="container">
                        <h1 className="title">{`${(courseDetails.courseNumber) ? courseDetails.courseNumber + ': ' : ''} ${courseDetails.title}`}</h1>
                        {courseDetails.courseSubtitle && <h2 className="subtitle">{courseDetails.courseSubtitle}</h2>}
                        <h2 className="title-secondary">
                            <div className="headline-label">Instructor{courseDetails.instructors.length > 1 ? 's': ''}</div>
                            {courseDetails.instructors.map((instructor, i) => {
                                // if more than one instructor
                                if(i !== 0){
                                    return(' & ' + instructor.Name)
                                } else {
                                    return (instructor.Name)
                                }
                                })}
                        </h2>
                    </div>
                </div>

                {/* COURSE SLIDES */}
                <div ref={divRef} className={`feature-hero-slides ${hidePagination ? 'feature-hero-slides--single' : ''}`}>
                    {/* If we have pagination */}
                    { !hidePagination &&
                        <div className="feature-hero-slides__paging">
                            <div className="container">
                                <div className="headline-label">{distinctionName ? distinctionName : 'Curriculum Path'}</div>
                                <div className="swiper-pagination"></div>
                            </div>
                        </div>
                    }

                    <div className="feature-hero-slides__swiper">
                            {/* Slides */}
                            <Swiper
                                spaceBetween={0}
                                centeredSlides={true}
                                watchSlidesProgress={true}
                                initialSlide={courseIndex}
                                modules={[Navigation, Pagination]}
                                pagination={hidePagination ? false : swiperPagination}
                                navigation={hidePagination ? false : {
                                    nextEl: '.swiper-button-next',
                                    prevEl: '.swiper-button-prev',
                                }}
                                // update the active course on slide change
                                onSlideChange={(swiper) => {
                                    //props.setActiveCourseName(courseNames[swiper.activeIndex]);
                                    dispatch(changeActiveCourse(courseNames[swiper.activeIndex]));
                                    pauseAllVideos();
                                }}
                            >
                                {courseNames.map(key => {
                                    return (
                                        <SwiperSlide>
                                            <div className="slide-feature">
                                                {/* if video */}
                                                {courseDetailsProp[key].heroFeatureType === 'video' &&
                                                    getVideoEmbed(key)
                                                }
                                                {/* if image */}
                                                {courseDetailsProp[key].heroFeatureType === 'image' &&
                                                    <img src={courseDetailsProp[key].heroImage.sizes['course-feature']} width="740px;" height="416px" />
                                                }
                                                <div className="overlay prev-overlay"></div>
                                                <div className="overlay next-overlay"></div>
                                            </div>
                                        </SwiperSlide>
                                    )}
                                )}
                                {/* If we need navigation buttons */}
                                    <>
                                        <div className="swiper-button-prev"><ArrowSwiperLeft /> <span className="sw-nav-label-prev">{(prevCourse == -1) ? '' : paginationData[prevCourse].courseNumber}</span></div>
                                        <div className="swiper-button-next"><span className="sw-nav-label-next">{(nextCourse == paginationData.length) ? '' : paginationData[nextCourse].courseNumber}</span> <ArrowSwiperRight /></div>
                                    </>
                            </Swiper>
                    </div>
                </div>
                {/* COURSE META INFO */}
                <CourseMetaInfo courseDetails={courseDetails} />

                {/* FOOTER AREA */}
                <CourseHeroFooter courseDetails={courseDetails} />
                
            </section>
        </>
    );
}

export default TimeLineHero;